/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useCallback, useEffect, useMemo } from "react"
import { Row, Col, Card, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Category } from "types/models/menu"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { getUCMConfig } from "utils/config"
import { debounceSortHandler } from "utils/misc"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { SortableItemList } from "components/common"
import { sortMenuItemNewLogic } from "utils/sort_helper"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { MenuCategoryRow } from "./MenuCategoryRow"

interface IProps {
  hasAction: boolean
}

export const MenuOverviewCategories: FC<IProps> = ({ hasAction }: IProps) => {
  const dispatch = useDispatch()

  const { isEditAllowed } = useMenuEditorStatus()
  const {
    overview: { categories },
    merchantID,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID = "" },
    isUCS,
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  const selectedSection = useSelector(
    (state: AppState) => state?.menu?.overview?.section,
  )
  const selectedCategory = useSelector(
    (state: AppState) => state?.menu?.overview?.category,
  )

  const filteredCategories = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return categories.filter(category => {
        return getItemWithoutPhotoCount(category.items || []) !== 0
      })
    }
    return categories
  }, [itemFilter, categories])

  const handleMenuClick = (key: string) => {
    let VALUE = null

    switch (key) {
      case "rearrange":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showSortCategories: true,
          }),
        )
        VALUE = "REARRANGE"
        break
      case "multiple":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showItemSelection: true,
          }),
        )
        VALUE = "SELECT_MULTIPLE"
        break
      default:
        break
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickCategoryMore,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        NUM_CATEGORY: filteredCategories.length ?? 0,
        VALUE,
      },
    })
  }

  const handleSelectCategory = useCallback(
    (category: Category) => {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.clickCategoryName,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          CATEGORY_ID: category.categoryID ?? 0,
        },
      })

      dispatch(actionCreator.menu.selectCategory(category))
    },
    [dispatch, menuGroupID, merchantGroupID, merchantID],
  )

  const selectedCategoryID = selectedCategory?.categoryID // because react-hook linter would complain if we use optional-chaining in the dep list ?_?
  useEffect(() => {
    const selectedCategoryExistsInFilteredCategories = filteredCategories.find(
      ({ categoryID }) => selectedCategoryID === categoryID,
    )
    if (
      !selectedCategoryExistsInFilteredCategories &&
      filteredCategories.length > 0
    ) {
      handleSelectCategory(filteredCategories[0])
    }
  }, [filteredCategories, handleSelectCategory, selectedCategoryID])

  const renderRow = (category: Category) => {
    return <MenuCategoryRow showEdit={hasAction} category={category} />
  }

  const textMap = useTranslationsMap({
    rearrange: "menu.drawers.sort.categories",
    selectMultiple: "menu.actions.selectMultiple",
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = debounceSortHandler(
    (sortedCategories: Category[], newIndex: number) => {
      const sectionID = selectedSection?.sectionID ?? ""

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewRearrangeCategory,
        action: EventAction.submitRearrangeCategory,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          SECTION_ID: sectionID,
        },
      })

      const sorts = isUCS
        ? sortMenuItemNewLogic({
            newIndex,
            newList: sortedCategories,
            sortKey: "sortOrder",
            itemKey: "categoryID",
          })
        : sortedCategories.map((category, index) => ({
            resourceID: category.categoryID ?? "",
            sortOrder: sortedCategories.length - index,
          }))

      dispatch(
        actionCreator.menu.sortCategories({
          merchantID,
          sectionID,
          sorts,
          successMessage: textMap.sortCategoriesSuccess,
          merchantGroupID,
          menuGroupID,
        }),
      )
    },
  )

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
      title={
        <Row justify="space-between">
          <Col>{useTranslation("menu.headings.categories")}</Col>
          {hasAction && getUCMConfig("enableTableMenuButton") && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{
                  items: [
                    ...(isEditAllowed &&
                    !(
                      filteredCategories.length === 0 ||
                      (!isSellingTimeStructure && selectedSection === null)
                    )
                      ? [
                          {
                            key: "rearrange",
                            label: textMap.rearrange,
                            onClick: () => handleMenuClick("rearrange"),
                          },
                        ]
                      : []),
                    {
                      key: "multiple",
                      label: textMap.selectMultiple,
                      onClick: () => handleMenuClick("multiple"),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredCategories}
        renderRowContent={renderRow}
        useDragHandle
        isItemSelected={(category: Category) =>
          category.categoryID === selectedCategory?.categoryID
        }
        onRowClick={(e: any, category: Category) => {
          e.persist()
          // small hack to prevent selecting when 3-dots menu is clicked
          const isClickingMenuBtn = e.target.closest(".menuBtn") !== null

          if (!isClickingMenuBtn) handleSelectCategory(category)
        }}
        draggable={getUCMConfig("enableDraggableList")}
        onSort={({ newList, newIndex }) => {
          onSortEnd(newList, newIndex)
        }}
      />
    </Card>
  )
}
